/**
 * Shared HTTP client for the GuestGuru Planning API.
 *
 * The GuestGuru backend (Express/TS) exposes CRUD endpoints under /planning/:project_id/*.
 * This client wraps fetch() with the correct base URL, API key, and Bearer token headers.
 *
 * Environment variable:
 *   REACT_APP_GG_API_URL — base URL of the GuestGuru backend
 *     defaults to http://localhost:8083 (local) or auto-detected from current host
 */

import { STORAGE_KEYS } from '../constants/storageKeys';
import { ApiError, buildApiError } from '../utils/apiErrors';
export { ApiError };
import { endSessionIfExpired, getActiveProjectId } from '../utils/session';

export function getApiUrl(): string {
  return (
    (import.meta.env.VITE_GG_API_URL as string | undefined) ||
    (window.location.hostname === 'localhost'
      ? 'http://localhost:8083'
      : 'https://gg.ainaeco.uk')
  );
}

export function getApiKey(): string {
  const key = import.meta.env.VITE_ANS_WEB_API_KEY as string | undefined;
  if (!key) console.error('VITE_ANS_WEB_API_KEY is not configured — GuestGuru requests will be rejected.');
  return key || '';
}

export function getProjectId(): string {
  return getActiveProjectId()
    ?? (import.meta.env.VITE_PROJECT_ID as string | undefined)
    ?? (import.meta.env.VITE_WEDDING_ID as string | undefined)
    ?? '';
}

/**
 * Builds a versioned planning path: `/planning/:project_id{suffix}`.
 * Use this for every planning API call so the project id stays in one place.
 *
 * @example planningPath('/events')          → /planning/00000…/events
 * @example planningPath(`/events/${id}`)    → /planning/00000…/events/123
 */
export function planningPath(suffix: string): string {
  return `/planning/${getProjectId()}${suffix}`;
}

interface ApiOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** Extra request headers (e.g. `Idempotency-Key`). Never used to override auth headers. */
  headers?: Record<string, string>;
}

async function request<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, headers: extraHeaders } = options;
  const headers: Record<string, string> = {
    ...extraHeaders,
    'Content-Type': 'application/json',
    'x-api-key': getApiKey(),
  };
  // x-user-email is a fallback only — GuestGuru trusts it for viewer-level
  // access without a token, but never for privileged (planner/owner) writes.
  // The real credential is the GuestGuru JWT below. Only set it when a real
  // profile owner is actually stored — defaulting to a real person's email
  // let any unauthenticated browser session silently identify as them and
  // get viewer access with zero login.
  const profileOwner = localStorage.getItem(STORAGE_KEYS.PROFILE_OWNER_EMAIL);
  if (profileOwner) {
    headers['x-user-email'] = profileOwner;
  }
  // Real, role-bearing GuestGuru JWT (see STORAGE_KEYS.GG_AUTH_TOKEN) —
  // verified server-side, so GuestGuru trusts the role/workspace_id in it
  // directly instead of looking up x-user-email.
  const token = localStorage.getItem(STORAGE_KEYS.GG_AUTH_TOKEN);
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // A network-level failure rejects with a bare TypeError whose message is
  // the browser's own wording — "Failed to fetch" / "Load failed". That text
  // reached users verbatim once handlers started surfacing err.message, and
  // it says nothing they can act on. Aborts are deliberate and must keep
  // their own error so callers can still recognise them.
  let res: Response;
  try {
    res = await fetch(`${getApiUrl()}${path}`, {
      method,
      headers,
      body: body != null ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw ApiError.offline();
  }

  if (!res.ok) {
    // An expired login must show one clear sign-in prompt, not a page of failed screens.
    endSessionIfExpired(res.status, token);
    const errBody = await res.json().catch(() => null);
    throw buildApiError(res.status, errBody);
  }

  // 204 No Content
  if (res.status === 204) return undefined as unknown as T;

  return res.json();
}

// ── convenience helpers ──

export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>(path, { signal });
}

export function apiPost<T>(path: string, body: unknown, headers?: Record<string, string>): Promise<T> {
  return request<T>(path, { method: 'POST', body, headers });
}

export function apiPut<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PUT', body });
}

export function apiPatch<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: 'PATCH', body });
}

/**
 * Defaults to `void` so every existing `await apiDelete(path)` call is
 * unchanged, but is generic because not every DELETE is a 204: the asoebi
 * packing endpoint returns the recomputed checklist so un-ticking a box
 * doesn't need a follow-up GET to learn the new state.
 */
export function apiDelete<T = void>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

